import React, { Component } from 'react';
import ReactDOM from 'react-dom';
import moment from 'moment';
import moment2 from 'moment-timezone';
import DiaryDatePicker from './DiaryDatePicker';
import LocationPicker from '../multiLocation/LocationPicker';
import './CalendarCard.css';

// Calendar days, not 24h blocks: the day the clocks go back is 25 hours long,
// so ts + 24h lands on the same date and the strip showed it twice.
const addDays = (ts, n) => moment(ts).add(n, 'days').valueOf();

// Normalize a timestamp to midnight for reliable day comparison
const toDayKey = (ts) => {
    const d = new Date(ts);
    return new Date(d.getFullYear(), d.getMonth(), d.getDate()).getTime();
};

// Pre-format a date timestamp into the display strings we need
const formatDate = (ts) => {
    const d = new Date(ts);
    const dayKey = new Date(d.getFullYear(), d.getMonth(), d.getDate()).getTime();
    return {
        ts,
        dayKey,
        day: d.getDate().toString(),
        dayOfWeek: moment(ts).format("ddd"),
        monthYear: moment(ts).format("MMMM YYYY"),
    };
};

// Format a week starting from a Monday timestamp
const formatWeek = (mondayTs) => {
    const mon = moment(mondayTs);
    const sun = mon.clone().add(6, 'days');
    const sameMonth = mon.month() === sun.month();
    const label = sameMonth
        ? `${mon.format('D')} - ${sun.format('D MMM')}`
        : `${mon.format('D MMM')} - ${sun.format('D MMM')}`;
    return {
        ts: mondayTs,
        mondayKey: mon.format('YYYY-MM-DD'),
        label,
        monthYear: mon.format('MMMM YYYY'),
    };
};

class CalendarCard extends Component {
    constructor(props) {
        super(props);
        let currentDate = new Date(props?.bookingDate);
        var seconds = currentDate.getSeconds();
        var minutes = currentDate.getMinutes();
        var hour = currentDate.getHours();
        var getMilliseconds = currentDate.getMilliseconds();
        const timeZone = Intl.DateTimeFormat().resolvedOptions().timeZone;
        const currentDateInTimeZone = moment2.tz(props?.bookingDate, timeZone);
        let formattedDate = new Date(currentDateInTimeZone);
        formattedDate.setHours(hour);
        formattedDate.setMinutes(minutes);
        formattedDate.setSeconds(seconds);
        formattedDate.setMilliseconds(getMilliseconds);

        this.todayKey = toDayKey(Date.now());

        this.state = {
            currentDate: currentDate.getTime(),
            mobileDates: [],
            mobileWeeks: [],
            today: new Date().getTime(),
            selectDate: new Date(formattedDate).getTime(),
            isCalendarOpen: false,
            visibleMonth: null,
            settingsOpen: false,
        };
        this._settingsRef = React.createRef();

        this.scrollRef = React.createRef();
        this.weekScrollRef = React.createRef();
        this.isExtending = false;
        this.scrollRafId = null;
        this.handleDateClick = this.handleDateClick.bind(this);
    }

    handlePrevDay = () => {
        const newDate = addDays(this.state.selectDate, -1);
        this.setState({ currentDate: newDate, selectDate: newDate }, () => {
            this.renderDates();
            this.props.setCalenderDate(new Date(newDate));
        });
    }

    handleNextDay = () => {
        const newDate = addDays(this.state.selectDate, 1);
        this.setState({ currentDate: newDate, selectDate: newDate }, () => {
            this.renderDates();
            this.props.setCalenderDate(new Date(newDate));
        });
    }

    handlePrevWeek = () => {
        const currentMonday = moment(this.state.selectDate).startOf('isoWeek');
        const prevMonday = currentMonday.subtract(1, 'week').valueOf();
        this.setState({ currentDate: prevMonday, selectDate: prevMonday }, () => {
            this.renderWeeks();
            this.props.setCalenderDate(new Date(prevMonday));
        });
    }

    handleNextWeek = () => {
        const currentMonday = moment(this.state.selectDate).startOf('isoWeek');
        const nextMonday = currentMonday.add(1, 'week').valueOf();
        this.setState({ currentDate: nextMonday, selectDate: nextMonday }, () => {
            this.renderWeeks();
            this.props.setCalenderDate(new Date(nextMonday));
        });
    }

    renderWeeks = () => {
        const currentMonday = moment(this.state.currentDate).startOf('isoWeek');
        const mobileWeeks = [];
        for (let i = -3; i <= 8; i++) {
            const monday = currentMonday.clone().add(i, 'weeks');
            mobileWeeks.push(formatWeek(monday.valueOf()));
        }
        this.setState({ mobileWeeks });
    }

    handleWeekClick = (mondayTs) => {
        this.setState({ selectDate: mondayTs, currentDate: mondayTs });
        this.props.setCalenderDate(new Date(mondayTs));
    }

    handleWeekScroll = () => {
        const el = this.weekScrollRef.current;
        if (!el) return;
        const { scrollLeft, scrollWidth, clientWidth } = el;

        // Update visible month based on center-visible week
        if (this.scrollRafId) cancelAnimationFrame(this.scrollRafId);
        this.scrollRafId = requestAnimationFrame(() => {
            const pillWidth = 110;
            const centerIndex = Math.floor((scrollLeft + clientWidth / 2) / pillWidth);
            const { mobileWeeks } = this.state;
            if (mobileWeeks && mobileWeeks[centerIndex]) {
                const monthLabel = mobileWeeks[centerIndex].monthYear;
                if (this.state.visibleMonth !== monthLabel) {
                    this.setState({ visibleMonth: monthLabel });
                }
            }
        });

        if (this.isExtending) return;

        // Near right edge - add more weeks
        if (scrollWidth - scrollLeft - clientWidth < 200) {
            this.isExtending = true;
            const last = this.state.mobileWeeks[this.state.mobileWeeks.length - 1];
            const newWeeks = [];
            for (let i = 1; i <= 6; i++) {
                newWeeks.push(formatWeek(addDays(last.ts, 7 * i)));
            }
            this.setState({
                mobileWeeks: [...this.state.mobileWeeks, ...newWeeks]
            }, () => { this.isExtending = false; });
        }

        // Near left edge - add more weeks
        if (scrollLeft < 200) {
            this.isExtending = true;
            const first = this.state.mobileWeeks[0];
            const newWeeks = [];
            for (let i = 6; i >= 1; i--) {
                newWeeks.push(formatWeek(addDays(first.ts, -7 * i)));
            }
            const prevScrollWidth = scrollWidth;
            this.setState({
                mobileWeeks: [...newWeeks, ...this.state.mobileWeeks]
            }, () => {
                el.scrollLeft = scrollLeft + (el.scrollWidth - prevScrollWidth);
                this.isExtending = false;
            });
        }
    }

    handleJumpFourWeeks = () => {
        const newDate = addDays(this.state.currentDate, 28);
        this.setState({ currentDate: newDate, selectDate: newDate }, () => {
            this.renderDates();
            this.props.setCalenderDate(new Date(newDate));
        });
    }

    handleJumpSixWeeks = () => {
        const newDate = addDays(this.state.currentDate, 42);
        this.setState({ currentDate: newDate, selectDate: newDate }, () => {
            this.renderDates();
            this.props.setCalenderDate(new Date(newDate));
        });
    }

    renderDates = () => {
        const { currentDate } = this.state;
        const mobileDates = [];
        for (let i = -3; i <= 17; i++) {
            mobileDates.push(formatDate(addDays(currentDate, i)));
        }
        this.setState({ mobileDates });
    }

    // The month pill shows "September 2026" unless the icons beside it leave
    // too little room, then "Sep 26". Measured, not a breakpoint: how many
    // icons there are changes (setup's Bailey button comes and goes).
    _monthPillRef = React.createRef();
    _monthLongRef = React.createRef();
    _mobActionsRef = React.createRef();
    _fitMonth = () => {
        const pill = this._monthPillRef.current, long = this._monthLongRef.current, actions = this._mobActionsRef.current;
        if (!pill || !long || !actions || !pill.offsetParent) return;
        const shown = this.state.shortMonth ? pill.querySelector('.diary-mob-month-short') : long;
        const chrome = pill.offsetWidth - (shown ? shown.offsetWidth : 0);
        const room = actions.getBoundingClientRect().left - pill.getBoundingClientRect().left - 8;
        const shortMonth = long.offsetWidth + chrome > room;
        if (shortMonth !== !!this.state.shortMonth) this.setState({ shortMonth });
    };

    componentDidUpdate(prevProps, prevState) {
        if (prevState && prevState.visibleMonth !== this.state.visibleMonth) this._fitMonth();
        if (prevProps.viewMode !== this.props.viewMode) {
            if (this.props.viewMode === 'week') {
                this.renderWeeks();
            }
            // Reset visible month when switching modes
            this.setState({ visibleMonth: null });
        }
    }

    componentDidMount() {
        this.renderDates();
        this._fitMonth();
        if (typeof ResizeObserver !== 'undefined') {
            this._monthObserver = new ResizeObserver(() => this._fitMonth());
            [this._mobActionsRef.current, this._monthPillRef.current && this._monthPillRef.current.parentElement].forEach(el => el && this._monthObserver.observe(el));
        } else {
            window.addEventListener('resize', this._fitMonth);
        }
        // Listen for diary scroll to collapse/expand date strip on mobile
        this._diaryScrollTimer = setTimeout(() => {
            this._diaryScrollEl = document.querySelector('.new--diary--clndr');
            if (this._diaryScrollEl) {
                this._diaryScrollEl.addEventListener('scroll', this._handleDiaryScroll, { passive: true });
            }
        }, 500);
    }

    componentWillUnmount() {
        if (this._monthObserver) this._monthObserver.disconnect();
        window.removeEventListener('resize', this._fitMonth);
        if (this.scrollRafId) cancelAnimationFrame(this.scrollRafId);
        if (this._diaryScrollEl) {
            this._diaryScrollEl.removeEventListener('scroll', this._handleDiaryScroll);
        }
        if (this._diaryScrollTimer) clearTimeout(this._diaryScrollTimer);
    }

    // ── Scroll-driven collapse: pure DOM, no React re-renders ──
    _isCompact = false;

    _handleDiaryScroll = () => {
        if (window.innerWidth >= 768) return;
        if (!this._diaryScrollEl) return;
        var scrollTop = this._diaryScrollEl.scrollTop;
        if (!this._isCompact && scrollTop > 30) {
            this._isCompact = true;
            this._applyCollapse(true);
        } else if (this._isCompact && scrollTop < 10) {
            this._isCompact = false;
            this._applyCollapse(false);
        }
    }

    _applyCollapse = (collapsed) => {
        // All class toggles in one go, no setState — pure CSS transitions handle animation
        var dateScroll = this.scrollRef.current;
        if (dateScroll) dateScroll.classList.toggle('diary-strip--collapsed', collapsed);
        var weekScroll = this.weekScrollRef.current;
        if (weekScroll) weekScroll.classList.toggle('diary-strip--collapsed', collapsed);
        var headerEl = document.querySelector('.dairy-container-header');
        if (headerEl) headerEl.classList.toggle('diary-header--compact', collapsed);
        if (this._diaryScrollEl) this._diaryScrollEl.classList.toggle('diary-scroll--compact', collapsed);
        // Compact pill — toggled via CSS class on the toolbar, no React render needed
        var toolbar = document.querySelector('.diary-mob-toolbar');
        if (toolbar) toolbar.classList.toggle('diary-toolbar--compact', collapsed);
    }

    handleMobileScroll = () => {
        const el = this.scrollRef.current;
        if (!el) return;
        const { scrollLeft, scrollWidth, clientWidth } = el;

        // Update visible month based on center-visible date
        if (this.scrollRafId) cancelAnimationFrame(this.scrollRafId);
        this.scrollRafId = requestAnimationFrame(() => {
            const centerIndex = Math.floor((scrollLeft + clientWidth / 2) / 60);
            const { mobileDates } = this.state;
            if (mobileDates && mobileDates[centerIndex]) {
                const monthLabel = mobileDates[centerIndex].monthYear;
                if (this.state.visibleMonth !== monthLabel) {
                    this.setState({ visibleMonth: monthLabel });
                }
            }
        });

        if (this.isExtending) return;

        // Near right edge - add more dates forward
        if (scrollWidth - scrollLeft - clientWidth < 200) {
            this.isExtending = true;
            const last = this.state.mobileDates[this.state.mobileDates.length - 1];
            const newDates = [];
            for (let i = 1; i <= 14; i++) {
                newDates.push(formatDate(addDays(last.ts, i)));
            }
            this.setState({
                mobileDates: [...this.state.mobileDates, ...newDates]
            }, () => { this.isExtending = false; });
        }

        // Near left edge - add more dates backward
        if (scrollLeft < 200) {
            this.isExtending = true;
            const first = this.state.mobileDates[0];
            const newDates = [];
            for (let i = 14; i >= 1; i--) {
                newDates.push(formatDate(addDays(first.ts, -i)));
            }
            const prevScrollWidth = scrollWidth;
            this.setState({
                mobileDates: [...newDates, ...this.state.mobileDates]
            }, () => {
                el.scrollLeft = scrollLeft + (el.scrollWidth - prevScrollWidth);
                this.isExtending = false;
            });
        }
    }

    handleDateClick(selectDate) {
        this.setState({ selectDate, currentDate: selectDate });
        this.props.setCalenderDate(selectDate);
    }

    selectCurrentDate(currentSelectedDate) {
        const isWeek = this.props.viewMode === 'week';
        const target = isWeek ? moment(currentSelectedDate).startOf('isoWeek').valueOf() : currentSelectedDate;
        this.setState({
            currentDate: target,
            selectDate: target
        }, () => {
            this.renderDates();
            if (isWeek) this.renderWeeks();
            setTimeout(() => {
                const ref = isWeek ? this.weekScrollRef : this.scrollRef;
                if (ref.current) {
                    ref.current.scrollTo({ left: 0, behavior: 'smooth' });
                }
            }, 50);
        });
        this.props.setCalenderDate(new Date(target));
    }

    handleDateCalenderClick = (selectDate) => {
        const timeInSec = new Date(selectDate).getTime();
        const isWeek = this.props.viewMode === 'week';
        const target = isWeek ? moment(timeInSec).startOf('isoWeek').valueOf() : timeInSec;
        this.setState({
            selectDate: target,
            currentDate: target,
            isCalendarOpen: false
        }, () => {
            this.renderDates();
            if (isWeek) this.renderWeeks();
            setTimeout(() => {
                const ref = isWeek ? this.weekScrollRef : this.scrollRef;
                if (ref.current) {
                    ref.current.scrollTo({ left: 0, behavior: 'smooth' });
                }
            }, 50);
        });
        this.props.setCalenderDate(new Date(target));
    }

    render() {
        const { today, selectDate } = this.state;
        const selectDayKey = toDayKey(selectDate);
        const isWeekMode = this.props.viewMode === 'week';

        const dateButtons = (this.state.mobileDates || []).map(d => (
            <button
                key={d.ts}
                className={`diary-date-item ${d.dayKey === selectDayKey ? 'selected' : ''} ${d.dayKey === this.todayKey ? 'today' : ''}`}
                onClick={() => this.handleDateClick(d.ts)}
            >
                <div className="diary-date-circle">
                    <span className="diary-date-number">{d.day}</span>
                </div>
                <span className="diary-date-day">{d.dayOfWeek}</span>
            </button>
        ));

        // Week pills for mobile week view
        const selectedMonday = moment(selectDate).startOf('isoWeek').format('YYYY-MM-DD');
        const thisMonday = moment().startOf('isoWeek').format('YYYY-MM-DD');
        const weekButtons = (this.state.mobileWeeks || []).map(w => (
            <button
                key={w.ts}
                className={`diary-week-item ${w.mondayKey === selectedMonday ? 'selected' : ''} ${w.mondayKey === thisMonday ? 'today' : ''}`}
                onClick={() => this.handleWeekClick(w.ts)}
            >
                <span className="diary-week-label">{w.label}</span>
            </button>
        ));

        // Desktop week label
        const desktopWeekLabel = (() => {
            const mon = moment(selectDate).startOf('isoWeek');
            const sun = mon.clone().add(6, 'days');
            return mon.month() === sun.month()
                ? `${mon.format('ddd D')} - ${sun.format('ddd D MMM')}`
                : `${mon.format('ddd D MMM')} - ${sun.format('ddd D MMM')}`;
        })();

        // Mobile compact pill label
        const compactLabel = isWeekMode
            ? `${moment(selectDate).startOf('isoWeek').format('D')}-${moment(selectDate).startOf('isoWeek').add(6, 'days').format('D MMM')}`
            : moment(selectDate).format("ddd D");

        return (
            <React.Fragment>
                <div className="calendar_card calendar-card-mobile">
                    {/* Desktop: minimal single-row header */}
                    <div className="d-none d-md-flex desktop-toolbar">
                        <button className="desktop-hamburger" onClick={() => {
                            document.body.classList.toggle('nav_menu_open');
                        }}>
                            <svg width="18" height="18" viewBox="0 0 18 18" fill="none">
                                <path d="M3 5H15M3 9H15M3 13H15" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"/>
                            </svg>
                        </button>
                        <button className="desktop-today-btn" onClick={() => this.selectCurrentDate(today)}>Today</button>

                        <div className="desktop-date-nav">
                            <button className="desktop-nav-arrow" onClick={isWeekMode ? this.handlePrevWeek : this.handlePrevDay} aria-label={isWeekMode ? "Previous week" : "Previous day"}>
                                <svg width="12" height="12" viewBox="0 0 12 12" fill="none"><path d="M7.5 2.5L4 6L7.5 9.5" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/></svg>
                            </button>
                            <div className="desktop-date-picker-wrap" ref={el => { this._dateLabel = el; }}>
                                <span className={`desktop-date-label ${isWeekMode ? 'desktop-date-label--week' : ''}`} onClick={() => { this.setState({ isCalendarOpen: !this.state.isCalendarOpen }) }}>
                                    {isWeekMode ? desktopWeekLabel : moment(selectDate).format("dddd D MMM")}
                                    <svg className={`desktop-date-chevron ${this.state.isCalendarOpen ? 'open' : ''}`} width="10" height="10" viewBox="0 0 12 12" fill="none"><path d="M3 4.5L6 7.5L9 4.5" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/></svg>
                                </span>
                                {/* Desktop picker rendered outside via single instance below */}
                            </div>
                            <button className="desktop-nav-arrow" onClick={isWeekMode ? this.handleNextWeek : this.handleNextDay} aria-label={isWeekMode ? "Next week" : "Next day"}>
                                <svg width="12" height="12" viewBox="0 0 12 12" fill="none"><path d="M4.5 2.5L8 6L4.5 9.5" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/></svg>
                            </button>
                        </div>

                        <div className="desktop-toolbar-right">
                            <button className="desktop-action-btn desktop-search-btn" onClick={() => {
                                const e = new KeyboardEvent('keydown', { key: 'k', metaKey: true, ctrlKey: true, bubbles: true });
                                document.dispatchEvent(e);
                            }} aria-label="Search" title="Search (⌘K)">
                                <svg width="16" height="16" viewBox="0 0 18 18" fill="none">
                                    <circle cx="8" cy="8" r="6" stroke="currentColor" strokeWidth="1.5"/>
                                    <path d="M13 13L16 16" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"/>
                                </svg>
                            </button>
                            <button className="desktop-week-jump" onClick={this.handleJumpFourWeeks}>+4w</button>
                            <button className="desktop-week-jump" onClick={this.handleJumpSixWeeks}>+6w</button>
                            {this.props.onZoomIn && (
                                <div className="dv2-settings-wrap" ref={this._settingsRef}>
                                    <button
                                        className={`desktop-action-btn ${this.state.settingsOpen ? 'active' : ''}`}
                                        onClick={() => { document.body.click(); this.setState(prev => ({ settingsOpen: !prev.settingsOpen })); }}
                                        aria-label="Diary settings"
                                    >
                                        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                                            <path d="M12.22 2h-.44a2 2 0 0 0-2 2v.18a2 2 0 0 1-1 1.73l-.43.25a2 2 0 0 1-2 0l-.15-.08a2 2 0 0 0-2.73.73l-.22.38a2 2 0 0 0 .73 2.73l.15.1a2 2 0 0 1 1 1.72v.51a2 2 0 0 1-1 1.74l-.15.09a2 2 0 0 0-.73 2.73l.22.38a2 2 0 0 0 2.73.73l.15-.08a2 2 0 0 1 2 0l.43.25a2 2 0 0 1 1 1.73V20a2 2 0 0 0 2 2h.44a2 2 0 0 0 2-2v-.18a2 2 0 0 1 1-1.73l.43-.25a2 2 0 0 1 2 0l.15.08a2 2 0 0 0 2.73-.73l.22-.39a2 2 0 0 0-.73-2.73l-.15-.08a2 2 0 0 1-1-1.74v-.5a2 2 0 0 1 1-1.74l.15-.09a2 2 0 0 0 .73-2.73l-.22-.38a2 2 0 0 0-2.73-.73l-.15.08a2 2 0 0 1-2 0l-.43-.25a2 2 0 0 1-1-1.73V4a2 2 0 0 0-2-2z" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                                            <circle cx="12" cy="12" r="3" stroke="currentColor" strokeWidth="1.5"/>
                                        </svg>
                                    </button>
                                    {this.state.settingsOpen && ReactDOM.createPortal(
                                        <div className="dv2-settings-overlay" onClick={() => this.setState({ settingsOpen: false })} />,
                                        document.body
                                    )}
                                    {this.state.settingsOpen && (
                                            <div className="dv2-settings-dropdown">
                                                <div className="dv2-settings-row">
                                                    <span className="dv2-settings-label">Zoom</span>
                                                    <div className="dv2-settings-controls">
                                                        <button className="dv2-settings-btn" onClick={this.props.onZoomOut}>
                                                            <svg width="12" height="12" viewBox="0 0 12 12" fill="none"><path d="M2.5 6H9.5" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"/></svg>
                                                        </button>
                                                        <button className="dv2-settings-btn" onClick={this.props.onZoomIn}>
                                                            <svg width="12" height="12" viewBox="0 0 12 12" fill="none"><path d="M6 2.5V9.5M2.5 6H9.5" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"/></svg>
                                                        </button>
                                                    </div>
                                                </div>
                                                <div className="dv2-settings-row">
                                                    <span className="dv2-settings-label">Grid</span>
                                                    <div className="dv2-settings-segment">
                                                        {[5, 10, 15, 30, 60].map(d => (
                                                            <button
                                                                key={d}
                                                                className={`dv2-segment-btn ${this.props.slotDuration === d ? 'dv2-segment-btn--active' : ''}`}
                                                                onClick={() => this.props.onSlotDurationChange(d)}
                                                            >
                                                                {d}m
                                                            </button>
                                                        ))}
                                                    </div>
                                                </div>
                                            </div>
                                    )}
                                </div>
                            )}
                        </div>
                    </div>

                    {/* Mobile toolbar */}
                    <div className="d-flex d-md-none diary-mob-toolbar">
                        <button className={`diary-mob-month-pill${this.state.shortMonth ? ' is-short' : ''}`} ref={this._monthPillRef} onClick={() => { this.setState({ isCalendarOpen: !this.state.isCalendarOpen }) }}>
                            {/* The full month when it fits; "Sep 26" only when the icons
                                beside it leave too little room (see _fitMonth). */}
                            <span className="diary-mob-month-long" ref={this._monthLongRef}>{this.state.visibleMonth || moment(selectDate).format("MMMM YYYY")}</span>
                            <span className="diary-mob-month-short">{moment(this.state.visibleMonth || moment(selectDate).format("MMMM YYYY"), "MMMM YYYY").format("MMM YY")}</span>
                            <svg width="10" height="10" viewBox="0 0 12 12" fill="none"><path d="M3 4.5L6 7.5L9 4.5" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" /></svg>
                        </button>
                        {/* Compact date pill - visibility driven by CSS parent class */}
                        <button
                            className="diary-mob-compact-pill"
                            onClick={() => {
                                if (this._diaryScrollEl) {
                                    this._diaryScrollEl.scrollTo({ top: 0, behavior: 'smooth' });
                                }
                            }}
                        >
                            {compactLabel}
                        </button>
                        <div className="diary-mob-actions" ref={this._mobActionsRef}>
                            {/* Notifications (setup, posts ready to share) live in the side menu on phones and tablets */}
                            <LocationPicker slot="mobile" />
                            <button className='diary-mob-action-btn' onClick={() => this.selectCurrentDate(today)} aria-label="Go to today">
                                <svg width="16" height="16" viewBox="0 0 24 24" fill="none">
                                    <rect x="3" y="4" width="18" height="17" rx="3" stroke="currentColor" strokeWidth="1.7" />
                                    <path d="M3 9H21" stroke="currentColor" strokeWidth="1.7" />
                                    <path d="M8 2V5" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" />
                                    <path d="M16 2V5" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" />
                                    <circle cx="12" cy="15" r="1.5" fill="currentColor" />
                                </svg>
                            </button>
                            <button className='diary-mob-action-btn' onClick={() => {
                                const e = new KeyboardEvent('keydown', { key: 'k', metaKey: true, ctrlKey: true, bubbles: true });
                                document.dispatchEvent(e);
                            }} aria-label="Search">
                                <svg width="16" height="16" viewBox="0 0 18 18" fill="none">
                                    <circle cx="8" cy="8" r="6" stroke="currentColor" strokeWidth="1.5"/>
                                    <path d="M13 13L16 16" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"/>
                                </svg>
                            </button>
                            {(this.props.onZoomIn || this.props.onFilterClick) && (
                                <div className="dv2-settings-wrap dv2-settings-wrap--mobile">
                                    <button
                                        className={`diary-mob-action-btn ${this.state.settingsOpen ? 'active' : ''}`}
                                        onClick={() => this.setState(prev => ({ settingsOpen: !prev.settingsOpen }))}
                                        aria-label="Diary options"
                                    >
                                        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                                            <path d="M12.22 2h-.44a2 2 0 0 0-2 2v.18a2 2 0 0 1-1 1.73l-.43.25a2 2 0 0 1-2 0l-.15-.08a2 2 0 0 0-2.73.73l-.22.38a2 2 0 0 0 .73 2.73l.15.1a2 2 0 0 1 1 1.72v.51a2 2 0 0 1-1 1.74l-.15.09a2 2 0 0 0-.73 2.73l.22.38a2 2 0 0 0 2.73.73l.15-.08a2 2 0 0 1 2 0l.43.25a2 2 0 0 1 1 1.73V20a2 2 0 0 0 2 2h.44a2 2 0 0 0 2-2v-.18a2 2 0 0 1 1-1.73l.43-.25a2 2 0 0 1 2 0l.15.08a2 2 0 0 0 2.73-.73l.22-.39a2 2 0 0 0-.73-2.73l-.15-.08a2 2 0 0 1-1-1.74v-.5a2 2 0 0 1 1-1.74l.15-.09a2 2 0 0 0 .73-2.73l-.22-.38a2 2 0 0 0-2.73-.73l-.15.08a2 2 0 0 1-2 0l-.43-.25a2 2 0 0 1-1-1.73V4a2 2 0 0 0-2-2z" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                                            <circle cx="12" cy="12" r="3" stroke="currentColor" strokeWidth="1.5"/>
                                        </svg>
                                        {this.props.filterActive && <span className="dv2-cog-dot" />}
                                    </button>
                                    {this.state.settingsOpen && ReactDOM.createPortal(
                                        <>
                                            <div className="dv2-settings-overlay dv2-settings-overlay--sheet" onClick={() => this.setState({ settingsOpen: false })} />
                                            <div className="dv2-settings-sheet">
                                                <div className="dv2-sheet-title">Diary options</div>
                                                {this.props.onZoomIn && (
                                                    <>
                                                        <div className="dv2-settings-row">
                                                            <span className="dv2-settings-label">Zoom</span>
                                                            <div className="dv2-settings-controls">
                                                                <button className="dv2-settings-btn" onClick={this.props.onZoomOut}>
                                                                    <svg width="12" height="12" viewBox="0 0 12 12" fill="none"><path d="M2.5 6H9.5" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"/></svg>
                                                                </button>
                                                                <button className="dv2-settings-btn" onClick={this.props.onZoomIn}>
                                                                    <svg width="12" height="12" viewBox="0 0 12 12" fill="none"><path d="M6 2.5V9.5M2.5 6H9.5" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"/></svg>
                                                                </button>
                                                            </div>
                                                        </div>
                                                        <div className="dv2-settings-row">
                                                            <span className="dv2-settings-label">Grid</span>
                                                            <div className="dv2-settings-segment">
                                                                {[5, 10, 15, 30, 60].map(d => (
                                                                    <button
                                                                        key={d}
                                                                        className={`dv2-segment-btn ${this.props.slotDuration === d ? 'dv2-segment-btn--active' : ''}`}
                                                                        onClick={() => this.props.onSlotDurationChange(d)}
                                                                    >
                                                                        {d}m
                                                                    </button>
                                                                ))}
                                                            </div>
                                                        </div>
                                                    </>
                                                )}
                                                {this.props.onFilterClick && (
                                                    <button
                                                        className="dv2-sheet-filter-row"
                                                        onClick={() => this.setState({ settingsOpen: false }, this.props.onFilterClick)}
                                                    >
                                                        <svg width="18" height="18" viewBox="0 0 18 18" fill="none">
                                                            <path d="M2.25 4.5H15.75" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" />
                                                            <path d="M4.5 9H13.5" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" />
                                                            <path d="M6.75 13.5H11.25" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" />
                                                        </svg>
                                                        <span className="dv2-sheet-filter-label">Staff filter</span>
                                                        {this.props.filterActive && <span className="dv2-sheet-tag">On</span>}
                                                        <svg className="dv2-sheet-chevron" width="14" height="14" viewBox="0 0 12 12" fill="none"><path d="M4.5 3L7.5 6L4.5 9" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/></svg>
                                                    </button>
                                                )}
                                            </div>
                                        </>,
                                        document.body
                                    )}
                                </div>
                            )}
                        </div>
                    </div>
                    {/* Single date picker instance - auto-detects mobile/desktop */}
                    <DiaryDatePicker
                        selected={selectDate}
                        open={this.state.isCalendarOpen}
                        onChange={(date) => this.handleDateCalenderClick(date)}
                        onClose={() => this.setState({ isCalendarOpen: false })}
                        anchorEl={this._dateLabel}
                    />

                    {/* Mobile date/week scroll - morph driven by diary scroll position */}
                    {isWeekMode ? (
                        <div
                            ref={this.weekScrollRef}
                            className="d-md-none diary-mob-date-scroll diary-mob-week-scroll"
                            onScroll={this.handleWeekScroll}
                        >
                            {weekButtons}
                        </div>
                    ) : (
                        <div
                            ref={this.scrollRef}
                            className="d-md-none diary-mob-date-scroll"
                            onScroll={this.handleMobileScroll}
                        >
                            {dateButtons}
                        </div>
                    )}
                </div>
            </React.Fragment>
        );
    }
}

export default CalendarCard;
